import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import Lightbox from 'react-image-lightbox';
import AssetUploadInput from './AssetUploadInput';
import { AssetUpload, ProjectAsset } from '../../../utils/api/projectAsset';

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif|tiff?)$/i;

export const isPhoto = (u: AssetUpload) =>
  u.upload_type === 'photo' ||
  !!u.upload?.mime_type?.startsWith('image/') ||
  (!!u.upload?.name && IMAGE_EXT.test(u.upload.name));

type PhotoFilter =
  | 'all'
  | 'by-inspection'
  | 'general'
  | 'this'
  | 'same-form'
  | 'any-form';

const PROJECT_FILTERS: { key: PhotoFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'by-inspection', label: 'By Inspection' },
  { key: 'general', label: 'General' },
];

// "most recent" is the single latest earlier inspection, picked by the server.
const INSPECTION_FILTERS: { key: PhotoFilter; label: string }[] = [
  { key: 'this', label: 'This Inspection' },
  { key: 'same-form', label: 'This Form, Most Recent' },
  { key: 'any-form', label: 'All Forms, Most Recent' },
  { key: 'all', label: 'All Photos' },
];

/** Form name and date for a photo from another inspection; "Project photo" for project-level uploads. */
const sourceLabel = (u: AssetUpload) =>
  u.inspection
    ? `${u.inspection.inspection_template?.name || 'Inspection'} · ${dayjs(
        u.inspection.created_date,
      ).format('MM/DD/YYYY')}`
    : 'Project photo';

interface IAssetPhotosProps {
  photos: AssetUpload[];
  canEdit: boolean;
  /** Set inside an inspection: switches to the inspection filters and labels. */
  inspectionId?: number;
  previous?: ProjectAsset['previous_inspections'];
  downloadUrl: (u: AssetUpload) => string;
  onUpload: (rec: { id: number; mime_type?: string }) => void;
  onRemove: (u: AssetUpload) => void;
}

const AssetPhotos: React.FC<IAssetPhotosProps> = ({
  photos,
  canEdit,
  inspectionId,
  previous,
  downloadUrl,
  onUpload,
  onRemove,
}) => {
  const filters = inspectionId ? INSPECTION_FILTERS : PROJECT_FILTERS;
  const [filter, setFilter] = useState<PhotoFilter>(filters[0].key);
  const [lightbox, setLightbox] = useState<{
    urls: string[];
    idx: number;
  } | null>(null);

  const visible = useMemo(() => {
    const from = (id?: number | null) =>
      photos.filter((p) => !!id && p.inspection_id === id);

    switch (filter) {
      case 'this':
        return from(inspectionId);
      case 'same-form':
        return from(previous?.same_form);
      case 'any-form':
        return from(previous?.any_form);
      case 'general':
        return photos.filter((p) => !p.inspection_id);
      case 'by-inspection':
        return photos.filter((p) => p.inspection_id);
      default:
        return photos;
    }
  }, [photos, filter, inspectionId, previous]);

  return (
    <div className='asset-section__row'>
      <div className='asset-section__col-head'>
        <h5>Photos</h5>
        {canEdit && (
          <AssetUploadInput
            onUpload={onUpload}
            imageDimensions={{
              width: 20,
              height: 20,
            }}
            allowedTypes={[
              'image/jpg',
              'image/jpeg',
              'image/png',
              'application/pdf',
            ]}
            className='asset-section__upload'
            maxFileSize={75000000}
          />
        )}
      </div>

      {photos.length > 0 && (
        <div className='asset-section__photo-filter'>
          {filters.map(({ key, label }) => (
            <button
              key={key}
              type='button'
              className={filter === key ? 'is-active' : ''}
              onClick={() => setFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {visible.length === 0 ? (
        <p className='asset-section__empty'>No photos.</p>
      ) : (
        <div className='asset-section__photos'>
          {visible.map((p, i) => (
            <button
              key={p.id}
              type='button'
              className='asset-section__photo'
              onClick={() =>
                setLightbox({ urls: visible.map(downloadUrl), idx: i })
              }
            >
              <img src={downloadUrl(p)} alt={p.upload?.name || 'photo'} />
              {inspectionId && p.inspection_id !== inspectionId && (
                <span className='asset-section__photo-source'>
                  {sourceLabel(p)}
                </span>
              )}
              <span className='asset-section__photo-name'>
                {p.upload?.name || `Photo ${p.id}`}
              </span>
              {canEdit && (
                <span
                  className='asset-section__photo-remove'
                  role='button'
                  aria-label='Remove photo'
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(p);
                  }}
                >
                  ×
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {lightbox && (
        <Lightbox
          mainSrc={lightbox.urls[lightbox.idx]}
          nextSrc={
            lightbox.urls.length > 1
              ? lightbox.urls[(lightbox.idx + 1) % lightbox.urls.length]
              : undefined
          }
          prevSrc={
            lightbox.urls.length > 1
              ? lightbox.urls[
                  (lightbox.idx - 1 + lightbox.urls.length) %
                    lightbox.urls.length
                ]
              : undefined
          }
          onCloseRequest={() => setLightbox(null)}
          onMoveNextRequest={() =>
            setLightbox((p) => p && { ...p, idx: (p.idx + 1) % p.urls.length })
          }
          onMovePrevRequest={() =>
            setLightbox(
              (p) =>
                p && { ...p, idx: (p.idx - 1 + p.urls.length) % p.urls.length },
            )
          }
        />
      )}
    </div>
  );
};

export default AssetPhotos;
